import { SxProps, Theme } from '@mui/material';

export interface ResumeStyle {
  name: string;
  description: string;
  headerStyle: SxProps<Theme>;
  footerStyle: SxProps<Theme>;
  contentStyle: SxProps<Theme>;
  markdownStyle: SxProps<Theme>;
  color: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
  };
}

const generateResumeStyles = (): Record<string, ResumeStyle> => ({
  classic: {
    name: 'Classic',
    description: 'Traditional, professional serif design',
    headerStyle: {
      display: 'flex',
      flexDirection: 'row',
      fontFamily: '"Times New Roman", Times, serif',
      borderBottom: '2px solid #2c3e50',
      paddingBottom: 2,
      marginBottom: 3,
    } as SxProps<Theme>,
    footerStyle: {
      fontFamily: '"Times New Roman", Times, serif',
      borderTop: '2px solid #2c3e50',
      paddingTop: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'uppercase',
      alignContent: 'center',
      fontSize: '0.8rem',
      pb: 2,
      mb: 2,
    } as SxProps<Theme>,
    contentStyle: {
      fontFamily: '"Times New Roman", Times, serif',
      lineHeight: 1.6,
      color: '#2c3e50',
    },
    markdownStyle: {
      fontFamily: '"Times New Roman", Times, serif',
      '& h1, & h2, & h3': {
        fontFamily: '"Times New Roman", Times, serif',
        color: '#2c3e50',
        borderBottom: '1px solid #bdc3c7',
        paddingBottom: 1,
        marginBottom: 2,
      },
      '& p, & li': { lineHeight: 1.6, marginBottom: 1 },
      '& ul': { paddingLeft: 3 },
    } as SxProps<Theme>,
    color: {
      primary: '#2c3e50',
      secondary: '#34495e',
      accent: '#3498db',
      text: '#2c3e50',
      background: '#ffffff',
    },
  },
  modern: {
    name: 'Modern',
    description: 'Clean, minimalist sans-serif layout',
    headerStyle: {
      display: 'flex',
      flexDirection: 'row',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      borderLeft: '4px solid #3498db',
      paddingLeft: 2,
      marginBottom: 3,
      backgroundColor: '#f8f9fa',
      padding: 2,
      borderRadius: 1,
    } as SxProps<Theme>,
    footerStyle: {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      borderLeft: '4px solid #3498db',
      backgroundColor: '#f8f9fa',
      paddingTop: 2,
      borderRadius: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'uppercase',
      alignContent: 'center',
      fontSize: '0.8rem',
      pb: 2,
      mb: 2,
    } as SxProps<Theme>,
    contentStyle: {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      lineHeight: 1.5,
      color: '#2c3e50',
    },
    markdownStyle: {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '& h1, & h2, & h3': {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        color: '#3498db',
        fontWeight: 300,
        marginBottom: 1.5,
      },
      '& h1': { fontSize: '1.75rem' },
      '& h2': { fontSize: '1.5rem' },
      '& h3': { fontSize: '1.25rem' },
      '& p, & li': { lineHeight: 1.5, marginBottom: 0.75 },
      '& ul': { paddingLeft: 2.5 },
    } as SxProps<Theme>,
    color: {
      primary: '#3498db',
      secondary: '#2c3e50',
      accent: '#e74c3c',
      text: '#2c3e50',
      background: '#ffffff',
    },
  },
  corporate: {
    name: 'Corporate',
    description: 'Formal, structured business format',
    headerStyle: {
      display: 'flex',
      flexDirection: 'row',
      fontFamily: '"Arial", sans-serif',
      border: '2px solid #34495e',
      padding: 2.5,
      marginBottom: 3,
      backgroundColor: '#ecf0f1',
    } as SxProps<Theme>,
    footerStyle: {
      fontFamily: '"Arial", sans-serif',
      border: '2px solid #34495e',
      backgroundColor: '#ecf0f1',
      paddingTop: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'uppercase',
      alignContent: 'center',
      fontSize: '0.8rem',
      pb: 2,
      mb: 2,
    } as SxProps<Theme>,
    contentStyle: {
      fontFamily: '"Arial", sans-serif',
      lineHeight: 1.2,
      fontSize: '0.875rem',
      color: '#2c3e50',
    },
    markdownStyle: {
      fontFamily: '"Arial", sans-serif',
      '& h1, & h2, & h3': {
        fontFamily: '"Arial", sans-serif',
        color: '#34495e',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        fontSize: '0.875rem',
        letterSpacing: '1px',
        marginBottom: 1.5,
        borderBottom: '1px solid #bdc3c7',
        paddingBottom: 0.5,
      },
      '& h1': { fontSize: '1rem' },
      '& h2': { fontSize: '0.875rem' },
      '& h3': { fontSize: '0.75rem' },
      '& p, & li': { lineHeight: 1.4, marginBottom: 0.75, fontSize: '0.75rem' },
      '& div': { fontSize: '0.75rem' },
      '& ul': { paddingLeft: 2 },
    } as SxProps<Theme>,
    color: {
      primary: '#34495e',
      secondary: '#2c3e50',
      accent: '#95a5a6',
      text: '#2c3e50',
      background: '#ffffff',
    },
  },
});

export const resumeStyles: Record<string, ResumeStyle> = generateResumeStyles();

export function resolveColor(
  style: ResumeStyle,
  key: 'primary' | 'secondary' | 'accent' | 'text'
): string {
  return style.name === 'creative' ? '#ffffff' : style.color[key];
}
